import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth, useTheme } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Sun, Moon, Eye, EyeOff, Loader2 } from 'lucide-react';

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (user) {
      const redirect = searchParams.get('redirect');
      navigate(redirect ? '/' + redirect : '/chat');
    }
  }, [user, navigate, searchParams]);

  // If someone navigates here with ?mode=register, route them to the
  // proper Request Access flow. QueryVerse is invitation-based; no self-serve signup.
  useEffect(() => {
    if (searchParams.get('mode') === 'register') {
      navigate('/request-access', { replace: true });
    }
  }, [searchParams, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      toast.success('Welcome back!');
      const redirect = searchParams.get('redirect');
      navigate(redirect ? '/' + redirect : '/chat');
    } catch (error) {
      // Normalise: prefer a clean status-based message over backend's raw detail
      // when the detail looks like an infra error but the cause is auth.
      // 423 lockout payloads from the backend already carry user-friendly copy.
      const msg = error.message || 'Sign-in failed.';
      const normalised = /unreachable|network|connection dropped|fetch|cors/i.test(msg)
        ? 'Service temporarily unavailable. Please try again in a moment.'
        : msg;
      setErrorMsg(normalised);
      toast.error(normalised);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    // Google OAuth via backend — requires GOOGLE_CLIENT_ID configured on the server.
    const callback = `${window.location.origin}/auth/callback`;
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google?redirect=${encodeURIComponent(callback)}`;
  };

  const handleInputChange = (e) => {
    setFormData(function (prev) {
      return Object.assign({}, prev, { [e.target.name]: e.target.value });
    });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <nav className="px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img
            src="/queryverse-logo.png"
            alt="QueryVerse"
            className="h-10"
          />
          <span className="text-[10px] text-muted-foreground hidden sm:block italic">
            a product by AppCenn Pvt Ltd
          </span>
        </Link>
        <button
          onClick={toggleTheme}
          className="p-2.5 rounded-full hover:bg-secondary transition-colors"
          data-testid="auth-theme-toggle"
        >
          {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </button>
      </nav>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-medium">Welcome back</h1>
            <p className="mt-2 text-sm text-muted-foreground">Sign in to continue</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {errorMsg && (
              <div
                role="alert"
                data-testid="auth-error-banner"
                className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
              >
                {errorMsg}
              </div>
            )}
            <Button
              type="button"
              onClick={handleGoogleSignIn}
              variant="outline"
              className="w-full h-11 rounded-full font-medium gap-2"
              data-testid="auth-google-btn"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
              </svg>
              Continue with Google
            </Button>
            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center text-xs uppercase tracking-wider text-muted-foreground">
                <span className="bg-background px-2">or with email</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm">
                Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@company.com"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="h-11 rounded-xl"
                data-testid="auth-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  minLength={6}
                  className="h-11 rounded-xl pr-11"
                  data-testid="auth-password-input"
                />
                <button
                  type="button"
                  onClick={function () { setShowPassword(!showPassword); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="password-toggle"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 rounded-full font-medium"
              disabled={loading}
              data-testid="auth-submit-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign in'}
            </Button>

            <div className="text-center">
              <Link
                to="/auth/forgot"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="auth-forgot-password-link"
              >
                Forgot your password?
              </Link>
            </div>
          </form>

          <div className="mt-6 text-center text-sm text-muted-foreground" data-testid="auth-request-access-link">
            Don't have access yet?{' '}
            <Link to="/request-access" className="text-primary font-medium hover:underline">
              Request access
            </Link>
          </div>
        </div>
      </main>

      <footer className="px-6 py-4 text-center">
        <p className="text-xs text-muted-foreground">
          By continuing, you agree to our Terms and Privacy Policy.
        </p>
      </footer>
    </div>
  );
};

export default AuthPage;
